import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { auth, db } from '../firebaseConfig';
import { apiGet, apiPost } from '../utils/backendApi';
import BrainMap, { BrainNeighbourhood } from '../components/BrainMap';
import ContextMap from '../components/ContextMap';
import BrainHero from '../components/BrainHero';
import BrainThink from '../components/BrainThink';
import { Compare } from '../components/ThinkViews';
import HowIKnew from '../components/HowIKnew';
import { mermaidCode } from '../components/Mermaid';
import { citationHref } from '../utils/citationLink';
import { backendBase } from '../utils/backendApi';
import './AdminBrain.css';

// The Brain (/admin/brain) — super-admin view of the compiled project brain.
// Reads the Firestore mirror written by scripts/sync-brain.mjs (brainPages +
// brainMeta/*; rules allow founders only). Purpose: see EVERYTHING the brain
// knows and, more importantly, where it is thin — lint findings, stale or
// orphan pages, code the wiki never mentions, index drift.

const TYPE_ORDER = ['system', 'decision', 'lesson', 'runbook', 'concept'];
const TYPE_LABEL = { system: 'Systems', decision: 'Decisions', lesson: 'Lessons', runbook: 'Runbooks', concept: 'Concepts' };
const KIND_LABEL = {
  'broken-link': 'Broken wikilinks',
  'index-missing': 'Missing from index',
  'index-dup': 'Duplicated in index',
  frontmatter: 'Frontmatter problems',
  'type-mismatch': 'Type / folder mismatch',
  'bad-status': 'Invalid status',
  'bad-date': 'Invalid date',
  orphan: 'Orphans (no inbound links)',
  stale: 'Stale (> 90 days)',
  thin: 'Thin pages',
  'missing-section': 'Missing required sections',
  'low-confidence': 'Low confidence',
  'source-missing': 'Sources pointing at missing files',
};
const SEV_ORDER = { error: 0, warn: 1, info: 2 };

const fmtAgo = (iso) => {
  if (!iso) return '—';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 90) return `${s}s ago`;
  if (s < 5400) return `${Math.round(s / 60)}m ago`;
  if (s < 172800) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

// [[slug]] / [[slug|label]] → markdown links to the in-app reader path.
// (A custom `wiki:` scheme would be stripped by react-markdown's URL
// sanitizer; a plain path survives it.)
const PAGE_PATH = '/admin/brain/page/';
const wikify = (md) =>
  String(md || '').replace(/\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g, (_m, slug, label) => `[${label || slug}](${PAGE_PATH}${slug.trim()})`);

function useBrain() {
  const [state, setState] = useState({ loading: true, error: '', summary: null, pages: null, log: null, index: null, schema: null, liveness: null, graph: null });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [summary, pagesSnap, log, index, schema, liveness, graphDoc] = await Promise.all([
          db.collection('brainMeta').doc('summary').get(),
          db.collection('brainPages').get(),
          db.collection('brainMeta').doc('log').get(),
          db.collection('brainMeta').doc('index').get(),
          db.collection('brainMeta').doc('schema').get(),
          db.collection('brainMeta').doc('liveness').get().catch(() => ({ exists: false })),
          db.collection('brainMeta').doc('graph').get().catch(() => ({ exists: false })),
        ]);
        let graph = null;
        try { graph = graphDoc.exists ? JSON.parse(graphDoc.data().graph) : null; } catch { graph = null; }
        if (!alive) return;
        const pages = {};
        pagesSnap.forEach((d) => { pages[d.id] = d.data(); });
        setState({
          loading: false, error: '',
          summary: summary.exists ? summary.data() : null,
          pages, log: log.exists ? log.data() : null,
          index: index.exists ? index.data() : null,
          schema: schema.exists ? schema.data() : null,
          liveness: liveness.exists ? liveness.data() : null,
          graph,
        });
      } catch (err) {
        if (!alive) return;
        const denied = /permission|insufficient/i.test(err.message || '');
        setState((s) => ({ ...s, loading: false, error: denied ? 'Super-admin access required.' : err.message }));
      }
    })();
    return () => { alive = false; };
  }, [tick]);
  return { ...state, reload: () => setTick((t) => t + 1) };
}

function PageLink({ slug, pages, children, className = '' }) {
  const exists = !!pages?.[slug];
  return (
    <Link to={`/admin/brain/page/${slug}`} className={`ab-link ${exists ? '' : 'is-missing'} ${className}`} title={exists ? pages[slug].title : `[[${slug}]] — no such page`}>
      {children || slug}
    </Link>
  );
}

function Markdown({ text, pages }) {
  const navigate = useNavigate();
  const components = useMemo(() => ({
    a: ({ href, children }) => {
      if (href && href.startsWith(PAGE_PATH)) {
        const slug = href.slice(PAGE_PATH.length);
        const exists = !!pages?.[slug];
        return (
          <a
            href={href}
            className={`ab-link ${exists ? '' : 'is-missing'}`}
            onClick={(e) => { e.preventDefault(); navigate(href); }}
          >
            {children}
          </a>
        );
      }
      const external = /^https?:/i.test(href || '');
      return <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined}>{children}</a>;
    },
    code: mermaidCode,
  }), [pages, navigate]);
  return (
    <div className="ab-md">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{wikify(text)}</ReactMarkdown>
    </div>
  );
}

function Sev({ s }) {
  return <span className={`ab-sev is-${s}`}>{s}</span>;
}

function Stat({ label, value, sub, tone, onClick }) {
  return (
    <button type="button" className={`ab-stat ${tone ? `is-${tone}` : ''} ${onClick ? 'is-clickable' : ''}`} onClick={onClick} disabled={!onClick}>
      <span className="ab-stat-value">{value}</span>
      <span className="ab-stat-label">{label}</span>
      {sub ? <span className="ab-stat-sub">{sub}</span> : null}
    </button>
  );
}

// ---- Liveness ----------------------------------------------------------------
// Three verdicts for the plane's own brain, from brainMeta/liveness (written
// by sync-brain.mjs) — same rules as brainVerdicts() in
// scripts/lib/brain-summary.mjs: populated (pages, lint clean), updating (a
// dated log entry in the last 30 days, few inbox drafts), current (the read
// model was synced recently and the RAG mirror has nothing pending).
const dayAge = (d) => (d ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : null);
export function planeVerdicts(summary, l) {
  const v = {};
  const pages = summary?.totals?.pages || 0;
  const errors = l?.errors ?? summary?.issueCounts?.error ?? 0;
  if (!pages) v.populated = { ok: false, level: 'down', reason: 'no wiki pages synced' };
  else if (errors) v.populated = { ok: false, level: 'warn', reason: `${pages} pages, ${errors} lint error${errors === 1 ? '' : 's'}` };
  else v.populated = { ok: true, level: 'ok', reason: `${pages} pages, ${summary?.totals?.logEntries || 0} log entries` };
  const age = dayAge(l?.lastLogEntry);
  if (age == null) v.updating = { ok: false, level: 'warn', reason: 'no dated log entry' };
  else if (age > 30) v.updating = { ok: false, level: 'warn', reason: `last log entry ${age} days ago` };
  else v.updating = { ok: true, level: 'ok', reason: `last log entry ${age === 0 ? 'today' : `${age}d ago`}` };
  const pending = (l?.inboxPending || 0) + (l?.runtimeInboxPending || 0);
  if (v.updating.ok && pending >= 5) v.updating = { ok: false, level: 'warn', reason: `${pending} inbox drafts waiting for ingest` };
  const syncAge = dayAge(l?.syncedAt || summary?.syncedAt);
  if (l?.mirrorPending > 0) v.current = { ok: false, level: 'warn', reason: `${l.mirrorPending} knowledge mirror doc${l.mirrorPending === 1 ? '' : 's'} waiting to index` };
  else if (syncAge == null) v.current = { ok: false, level: 'warn', reason: 'never synced' };
  else if (syncAge > 7) v.current = { ok: false, level: 'warn', reason: `read model synced ${syncAge} days ago` };
  else v.current = { ok: true, level: 'ok', reason: `synced ${syncAge === 0 ? 'today' : `${syncAge}d ago`}${l?.gitSha ? ` from ${String(l.gitSha).slice(0, 7)}` : ''}` };
  return v;
}

function VerdictChips({ verdicts, compact = false }) {
  if (!verdicts) return <span className="cs-help">—</span>;
  return (
    <div className={`ab-verdicts ${compact ? 'is-compact' : ''}`}>
      {['populated', 'updating', 'current'].map((k) => {
        const v = verdicts[k];
        if (!v) return null;
        return (
          <span key={k} className={`ab-verdict is-${v.level || (v.ok ? 'ok' : 'warn')}`} title={v.reason}>
            <span className="ab-verdict-dot" aria-hidden="true" />
            <span className="ab-verdict-name">{k}</span>
            {!compact && <span className="ab-verdict-reason">{v.reason}</span>}
          </span>
        );
      })}
    </div>
  );
}

function LivenessCard({ summary, liveness }) {
  const v = planeVerdicts(summary, liveness);
  const l = liveness || {};
  return (
    <section className="cs-card ab-card ab-liveness">
      <h2>Is this brain alive? <span className="cs-help">the plane's own brain — populated, updating, current</span></h2>
      <VerdictChips verdicts={v} />
      <div className="ab-stats" style={{ marginTop: 10 }}>
        <Stat label="last log entry" value={l.lastLogEntry || '—'} sub={l.lastLogEntryAgeDays != null ? `${l.lastLogEntryAgeDays}d ago` : ''} />
        <Stat label="last page edit" value={l.lastPageUpdated || '—'} />
        <Stat label="brain committed" value={l.brainCommittedAt ? fmtAgo(l.brainCommittedAt) : '—'} sub={l.gitSha ? String(l.gitSha).slice(0, 7) : ''} />
        <Stat label="inbox" value={`${l.inboxPending ?? 0} + ${l.runtimeInboxPending ?? 0}`} sub="files · runtime drafts" tone={(l.inboxPending || 0) + (l.runtimeInboxPending || 0) >= 5 ? 'warn' : undefined} />
        <Stat label="RAG mirror" value={l.mirrorDocs ?? '—'} sub={l.mirrorPending ? `${l.mirrorPending} pending` : 'indexed'} tone={l.mirrorPending ? 'warn' : 'ok'} />
      </div>
      <p className="cs-help" style={{ marginTop: 8 }}>Every hosted app answers the same three questions on its own <code>/brain</code>; the fleet view lists them. <Link to="/admin/brain/fleet">Fleet brains →</Link></p>
    </section>
  );
}

// ---- Fleet: every hosted app's brain, as phoned home ------------------------
// Reads /apps/admin/instances (operator); each instance's monitor.brain is
// the kit's usageReporter brain block — verdicts, page counts, inbox, corpus
// drift. The deep link opens the app's own role-filtered /brain page.
const sameRef = (a, b) => { const x = String(a || ''); const y = String(b || ''); return !!x && !!y && (x.startsWith(y) || y.startsWith(x)); };
function FleetBrains() {
  const [state, setState] = useState({ loading: true, error: '', instances: [] });
  useEffect(() => {
    let alive = true;
    apiGet('/apps/admin/instances')
      .then((r) => { if (alive) setState({ loading: false, error: '', instances: (r.instances || r || []).filter((i) => i.status !== 'terminated') }); })
      .catch((err) => { if (alive) setState({ loading: false, error: err.message, instances: [] }); });
    return () => { alive = false; };
  }, []);
  const rows = useMemo(() => [...state.instances].sort((a, b) => {
    const bad = (i) => ['populated', 'updating', 'current'].filter((k) => i.monitor?.brain?.verdicts?.[k] && !i.monitor.brain.verdicts[k].ok).length;
    return bad(b) - bad(a) || String(a.name || a.slug).localeCompare(String(b.name || b.slug));
  }), [state.instances]);
  const withBrain = rows.filter((i) => i.monitor?.brain).length;
  const pulled = rows.filter((i) => i.monitor?.brain?.source === 'github').length;
  const attention = rows.filter((i) => ['populated', 'updating', 'current'].some((k) => i.monitor?.brain?.verdicts?.[k] && !i.monitor.brain.verdicts[k].ok)).length;
  return (
    <section className="cs-card ab-card">
      <h2>Fleet brains <span className="cs-help">{state.loading ? 'loading…' : `${rows.length} instances · ${withBrain} with a brain (${withBrain - pulled} phoned home · ${pulled} pulled) · ${attention} need attention`}</span></h2>
      {state.error ? <p className="cs-help">{state.error}</p> : null}
      <p className="cs-help">What each app's own brain says. Kit instances phone it home (kit <code>usageReporter</code> brain block, every 5 min); foundation apps that run outside the fleet are <em>pulled</em> from their GitHub repo every few hours (Admin › Apps › pull brain). Open an app's brain to read it as your seat there allows.</p>
      <table className="aa-table ab-table">
        <thead><tr><th>App</th><th>Env</th><th>Verdicts</th><th>Pages</th><th>Last entry</th><th>Inbox</th><th>Corpus</th><th>Self-test</th><th></th></tr></thead>
        <tbody>
          {rows.map((i) => {
            const b = i.monitor?.brain;
            const kitRef = i.monitor?.kitRef || i.repo?.kitRef || null;
            const drift = b?.corpusKitRef && kitRef && !sameRef(b.corpusKitRef, kitRef);
            const st = i.monitor?.selfTest;
            const pulledFromGit = b?.source === 'github';
            // Open brain: the app's own /brain page when it has one, else the
            // brain/ folder of the repo we pull from (foundation apps on an
            // older foundation have no Brain page yet).
            const url = i.profile?.inAppBrain !== false && i.urls?.app ? `${String(i.urls.app).replace(/\/$/, '')}/brain`
              : pulledFromGit && b.repo ? `https://github.com/${b.repo}/tree/main/brain` : null;
            const ageDays = pulledFromGit && b.pulledAt ? Math.round((Date.now() - b.pulledAt) / 864e5) : null;
            return (
              <tr key={`${i.orgId}-${i.appId}`}>
                <td>
                  <strong>{i.name || i.slug}</strong>
                  {pulledFromGit ? <span className="aa-tag" title={`pulled from ${b.repo}@${b.commit || '?'} ${b.pulledAt ? new Date(b.pulledAt).toLocaleString() : ''}`}>pulled · {String(b.commit || '').slice(0, 7)} · {ageDays === 0 ? 'today' : `${ageDays}d`}</span> : null}
                  <div className="cs-help aa-mono">{i.slug}</div>
                </td>
                <td>{i.envRole || 'prod'}</td>
                <td>{b?.verdicts ? <VerdictChips verdicts={b.verdicts} compact /> : <span className="cs-help">{b ? 'older kit — no verdicts' : 'no brain reported'}</span>}</td>
                <td>{pulledFromGit ? `${b.appPages} own` : b ? `${b.appPages ?? (b.lessons + b.decisions + b.runbooks)} app · ${b.kitPages ?? '—'} kit` : '—'}</td>
                <td className="aa-mono">{b?.lastEntryAt ? String(b.lastEntryAt).slice(0, 10) : b?.entries?.[0]?.date || '—'}</td>
                <td>{pulledFromGit ? <span className="cs-help" title="the brain inbox is a kit-instance mechanism">n/a</span> : b?.inboxPending ? <span className="ab-sev is-warn">{b.inboxPending}</span> : b ? '0' : '—'}</td>
                <td>{pulledFromGit ? <span className="cs-help" title="no kit knowledge corpus to drift — the app's own seeding is its own">n/a</span> : drift ? <span className="ab-sev is-warn" title={`seeded from ${b.corpusKitRef}, running ${kitRef}`}>stale</span> : b?.corpusKitRef ? <span className="ab-sev is-ok">matches</span> : '—'}{!pulledFromGit && b?.corpusPending ? <span className="cs-help"> · {b.corpusPending} pending</span> : null}</td>
                <td>{st ? <span className={`ab-sev ${st.ok ? 'is-ok' : 'is-error'}`}>{st.passed}/{st.total}</span> : pulledFromGit ? <span className="cs-help" title="foundation apps do not phone a self-test home">n/a</span> : '—'}</td>
                <td>{url ? <a className="cs-btn cs-btn-sm cs-btn-outline" href={url} target="_blank" rel="noreferrer">Open brain</a> : null}</td>
              </tr>
            );
          })}
          {!state.loading && !rows.length ? <tr><td colSpan={9} className="cs-help">No instances.</td></tr> : null}
        </tbody>
      </table>
    </section>
  );
}

// ---- Kit ships -------------------------------------------------------------
// What an App Kit instance receives from the kit's brain + docs — the ledger
// brainMeta/kitShip (api/kitShipWorker.js reads the kit repo on GitHub at the
// ref the fleet runs and at the next release's ref). Every row is a kit
// brain page; the Ships switch opens a kit PR that edits the page's
// frontmatter (`ship: instance`) — merging that PR is the release. Lessons
// never ship; a page that trips the leak denylist cannot be turned on until
// its lines are reworded (brain-lint would fail the PR).
const SHIP_FILTERS = [['all', 'All'], ['ships', 'Ships'], ['kit', 'Kit-only'], ['attention', 'Needs attention']];
const shortSha = (s) => String(s || '').slice(0, 7);

function KitShips() {
  const [state, setState] = useState({ loading: true, error: '', ledger: null, refs: null });
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState('');
  const [openLeaks, setOpenLeaks] = useState(null);
  const [notice, setNotice] = useState(null);
  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: '' }));
    return apiGet('/admin/brain/kit')
      .then((r) => setState({ loading: false, error: '', ledger: r.ledger, refs: r.refs }))
      .catch((err) => setState({ loading: false, error: err.message, ledger: null, refs: null }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const pull = async () => {
    setBusy('pull'); setNotice(null);
    try { const r = await apiPost('/admin/brain/kit/pull', {}); setState({ loading: false, error: '', ledger: r.ledger, refs: r.refs }); }
    catch (err) { setNotice({ kind: 'error', text: err.message }); }
    finally { setBusy(''); }
  };
  const flip = async (p, ship) => {
    if (p.lesson && ship) return;
    if (ship && (p.leaks.length || p.blurbLeaks.length)) { setOpenLeaks(openLeaks === p.slug ? null : p.slug); return; }
    const verb = ship ? 'start shipping' : 'stop shipping';
    if (!window.confirm(`Open a kit PR so “${p.title}” will ${verb} to App Kit instances?\n\nMerging that PR is the release.`)) return;
    setBusy(p.slug); setNotice(null);
    try {
      const r = await apiPost('/admin/brain/kit/ship', { slug: p.slug, ship });
      setNotice({ kind: 'ok', text: `PR #${r.pr.number} opened — ${r.pr.title}`, url: r.pr.url });
      setState((s) => ({ ...s, ledger: { ...s.ledger, pending: [...(s.ledger.pending || []), r.pr] } }));
    } catch (err) {
      setNotice({ kind: 'error', text: err.message });
    } finally { setBusy(''); }
  };

  const L = state.ledger;
  const next = L?.next; const fleet = L?.fleet;
  const pending = useMemo(() => new Map((L?.pending || []).map((p) => [p.slug, p])), [L]);
  const attention = (p) => (p.ships && (p.leaks.length || p.blurbLeaks.length)) || pending.has(p.slug);
  const rows = useMemo(() => {
    const all = next?.pages || [];
    if (filter === 'ships') return all.filter((p) => p.ships);
    if (filter === 'kit') return all.filter((p) => !p.ships);
    if (filter === 'attention') return all.filter(attention);
    return all;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [next, filter, pending]);
  const grouped = useMemo(() => {
    const g = {};
    for (const p of rows) (g[p.type] || (g[p.type] = [])).push(p);
    for (const t of Object.keys(g)) g[t].sort((a, b) => a.title.localeCompare(b.title));
    return g;
  }, [rows]);
  const blobUrl = (p) => (L?.repo && next?.commit ? `https://github.com/${L.repo}/blob/${next.commit}/${p.path}` : null);

  if (state.error) return <section className="cs-card ab-card"><h2>Kit ships</h2><p className="cs-help">{state.error}</p></section>;
  if (state.loading && !L) return <section className="cs-card ab-card"><h2>Kit ships</h2><p className="cs-help">Loading…</p></section>;
  if (!L?.next) {
    return (
      <section className="cs-card ab-card" data-nova-note="Kit ships: the ledger has not been pulled yet. Pull now reads the kit repo on GitHub.">
        <h2>Kit ships</h2>
        <p className="cs-help">Nothing pulled yet{state.refs?.error ? ` — ${state.refs.error}` : ''}. The worker reads the kit repo an hour after boot and every few hours after; pull it now to see what an instance receives.</p>
        {L?.error ? <p className="cs-help">Last attempt: {L.error}</p> : null}
        <button type="button" className="cs-btn cs-btn-sm" disabled={busy === 'pull'} onClick={pull}>{busy === 'pull' ? 'Pulling…' : 'Pull now'}</button>
        {notice ? <p className={`cs-help ${notice.kind === 'error' ? 'ab-notice-error' : ''}`}>{notice.text}</p> : null}
      </section>
    );
  }
  const c = next.counts; const d = L.delta || {};
  const deltaBits = [
    d.starts?.length ? `+${d.starts.length} will start shipping (${d.starts.join(', ')})` : null,
    d.added?.length ? `+${d.added.length} new shipped page${d.added.length > 1 ? 's' : ''} (${d.added.join(', ')})` : null,
    d.stops?.length ? `−${d.stops.length} will stop (${d.stops.join(', ')})` : null,
    d.removed?.length ? `−${d.removed.length} removed (${d.removed.join(', ')})` : null,
    d.leaks?.length ? `${d.leaks.length} leak count${d.leaks.length > 1 ? 's' : ''} changed (${d.leaks.map((x) => `${x.slug} ${x.from}→${x.to}`).join(', ')})` : null,
  ].filter(Boolean);
  const typeOrder = [...TYPE_ORDER.filter((t) => grouped[t]), ...Object.keys(grouped).filter((t) => !TYPE_ORDER.includes(t))];

  return (
    <section className="cs-card ab-card ab-kit" data-nova-note={`Kit ships: ${c.ships} of ${c.pages} kit brain pages and ${c.docsShipped} of ${c.docs} docs ship to App Kit instances at the next release (${next.ref}@${shortSha(next.commit)}); the fleet runs ${fleet.ref}@${shortSha(fleet.commit)}. Every row is a kit brain page; the Ships switch opens a kit PR that edits the page's frontmatter — it does not ship by itself, merging the PR is the release. Lessons never ship; pages with leak-denylist hits cannot be turned on.`}>
      <div className="ab-kit-head">
        <h2>Kit ships <span className="cs-help">{c.ships} ship · {c.kitOnly} kit-only · {c.lessons} lessons (never) · {c.docsShipped}/{c.docs} docs</span></h2>
        <div className="ab-kit-actions">
          <span className="cs-help">pulled {fmtAgo(new Date(L.pulledAt).toISOString())}</span>
          <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy === 'pull'} onClick={pull}>{busy === 'pull' ? 'Pulling…' : 'Pull now'}</button>
        </div>
      </div>
      <p className="cs-help">
        What an App Kit instance receives from the kit's brain and docs when it is stamped or swapped (<code>scripts/seed-knowledge.mjs</code>).
        {' '}Fleet runs <span className="aa-mono">{fleet.ref}@{shortSha(fleet.commit)}</span>; next release ships <span className="aa-mono">{next.ref}@{shortSha(next.commit)}</span>.
        {' '}The Ships switch opens a kit PR that edits the page's <code>ship:</code> frontmatter — merging it is the release; instances re-seed in their window and prune what stopped shipping.
      </p>
      {L.error ? <p className="cs-help ab-notice-error">Last pull failed: {L.error}</p> : null}
      {fleet.legacy ? <p className="ab-kit-delta"><strong>The fleet's kit ({fleet.ref}) predates the foundation tier:</strong> its seeder shipped every page that was not superseded — {fleet.counts.ships} pages including {fleet.counts.lessons} lessons, plus AGENTS.md, the index and the session log. Instances clean up when the tier reaches them.</p> : null}
      {next.legacy ? <p className="ab-kit-delta"><strong>The release branch ({next.ref}) predates the foundation tier too</strong> — the Ships switches are off until the tier's PR merges to it.</p> : null}
      {!L.sameRef && deltaBits.length ? <p className="ab-kit-delta"><strong>Next release vs fleet:</strong> {deltaBits.join(' · ')}</p> : null}
      {!L.sameRef && !deltaBits.length ? <p className="cs-help">Next release ships the same set the fleet runs.</p> : null}
      {notice ? <p className={`ab-kit-notice ${notice.kind === 'error' ? 'ab-notice-error' : 'ab-notice-ok'}`}>{notice.text}{notice.url ? <> — <a href={notice.url} target="_blank" rel="noreferrer">open PR</a></> : null}</p> : null}

      <div className="ab-filters ab-kit-filters">
        {SHIP_FILTERS.map(([k, label]) => (
          <button key={k} type="button" className={`ab-chip ${filter === k ? 'is-active' : ''}`} onClick={() => setFilter(k)}>
            {label}<span className="ab-count">{k === 'all' ? c.pages : k === 'ships' ? c.ships : k === 'kit' ? c.kitOnly : (next.pages || []).filter(attention).length}</span>
          </button>
        ))}
      </div>

      {typeOrder.map((t) => (
        <details key={t} className="ab-kit-group" open>
          <summary><span className={`ab-type is-${t}`}>{TYPE_LABEL[t] || t}</span> <span className="ab-count">{grouped[t].length}</span>{t === 'lesson' ? <span className="cs-help"> — lessons are the supplier's incident history and never ship</span> : null}</summary>
          <table className="aa-table ab-table ab-kit-table">
            <thead><tr><th>Page</th><th>Ships</th><th>Leaks</th><th>Updated</th><th>PR</th></tr></thead>
            <tbody>
              {grouped[t].map((p) => {
                const leaks = p.leaks.length + p.blurbLeaks.length;
                const pr = pending.get(p.slug);
                const disabled = busy === p.slug || next.legacy || (p.lesson && !p.ships) || !!pr;
                const title = next.legacy ? 'the release branch predates the foundation tier' : p.lesson ? 'lessons never ship' : pr ? `PR #${pr.number} is open for this page` : leaks && !p.ships ? 'reword the leak lines first (click to see them)' : p.ships ? 'open a PR to stop shipping this page' : 'open a PR to ship this page';
                return (
                  <React.Fragment key={p.slug}>
                    <tr className={p.ships ? 'is-ships' : ''}>
                      <td>
                        {blobUrl(p) ? <a className="ab-link" href={blobUrl(p)} target="_blank" rel="noreferrer">{p.title}</a> : <strong>{p.title}</strong>}
                        <div className="cs-help aa-mono">{p.slug}{p.status && p.status !== 'active' ? ` · ${p.status}` : ''}</div>
                      </td>
                      <td>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={p.ships}
                          className={`ab-switch ${p.ships ? 'is-on' : ''}`}
                          disabled={disabled}
                          title={title}
                          data-agent-guard="true"
                          onClick={() => flip(p, !p.ships)}
                        >
                          <span className="ab-switch-knob" />
                          <span className="ab-switch-label">{busy === p.slug ? '…' : p.ships ? 'ships' : 'kit-only'}</span>
                        </button>
                      </td>
                      <td>
                        {leaks ? <button type="button" className={`ab-sev ${p.ships ? 'is-error' : 'is-warn'} ab-sev-btn`} onClick={() => setOpenLeaks(openLeaks === p.slug ? null : p.slug)} title={p.ships ? 'a shipped page with leak hits — brain-lint fails' : 'would need rewording before it can ship'}>{leaks}</button> : <span className="cs-help">0</span>}
                      </td>
                      <td className="aa-mono">{p.updated || '—'}</td>
                      <td>{pr ? <a className="aa-tag" href={pr.url} target="_blank" rel="noreferrer" title={pr.title}>PR #{pr.number} · {pr.on ? 'ship' : 'keep'}</a> : null}</td>
                    </tr>
                    {openLeaks === p.slug && leaks ? (
                      <tr className="ab-kit-leaks"><td colSpan={5}>
                        {p.leaks.map((h) => <div key={`b${h.line}`}><span className="ab-sev is-warn">line {h.line}</span> <span className="cs-help">{h.why}</span> — <code>{h.text}</code></div>)}
                        {p.blurbLeaks.map((h, i) => <div key={`i${i}`}><span className="ab-sev is-warn">index blurb</span> <span className="cs-help">{h.why}</span> — <code>{h.text}</code></div>)}
                        <div className="cs-help">Reword these lines in the kit repo (instance-neutral phrasing; <code>sources</code> may still cite other repos — the seeder strips frontmatter).</div>
                      </td></tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </details>
      ))}
      {!rows.length ? <p className="cs-help">No pages match this filter.</p> : null}

      <details className="ab-kit-group">
        <summary><strong>Docs</strong> <span className="ab-count">{c.docsShipped}/{c.docs} ship</span><span className="cs-help"> — <code>docs/*.md</code> ship by default; the exclusions are the <code>DOCS_NOT_SHIPPED</code> list in <code>backend/shared/shipPolicy.js</code> (identical in both repos), not a switch</span></summary>
        <table className="aa-table ab-table ab-kit-table">
          <thead><tr><th>Doc</th><th>Ships</th><th>Leaks</th></tr></thead>
          <tbody>
            {next.docs.map((doc) => (
              <tr key={doc.name} className={doc.ships ? 'is-ships' : ''}>
                <td>{L.repo ? <a className="ab-link" href={`https://github.com/${L.repo}/blob/${next.commit}/docs/${doc.name}`} target="_blank" rel="noreferrer">{doc.name}</a> : doc.name}</td>
                <td>{doc.ships ? <span className="ab-sev is-ok">ships</span> : <span className="cs-help">kit-only</span>}</td>
                <td>{doc.leaks.length ? <span className={`ab-sev ${doc.ships ? 'is-error' : 'is-warn'}`} title={doc.leaks.map((h) => `line ${h.line}: ${h.why}`).join('\n')}>{doc.leaks.length}</span> : <span className="cs-help">0</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>

      <details className="ab-kit-group">
        <summary><strong>What an instance receives</strong> <span className="ab-count">{next.receives.length} knowledge docs</span><span className="cs-help"> — in the order the seeder writes them; ids as they appear in the instance's <code>knowledge</code> collection</span></summary>
        <ul className="ab-kit-receives">
          {next.receives.map((r) => <li key={r.id}><span className="aa-mono">{r.id}</span> <span className={`ab-type is-${r.kind}`}>{r.kind}</span> <span className="cs-help">{r.audience}</span></li>)}
        </ul>
      </details>

      {L.history?.length ? (
        <details className="ab-kit-group">
          <summary><strong>Flips</strong> <span className="ab-count">{L.history.length}</span><span className="cs-help"> — PRs opened from this screen</span></summary>
          <ul className="ab-kit-receives">
            {L.history.map((h) => <li key={`${h.number}`}><span className="aa-mono">{new Date(h.at).toISOString().slice(0, 16).replace('T', ' ')}</span> {h.by} — <a href={h.url} target="_blank" rel="noreferrer">PR #{h.number}</a> {h.title}</li>)}
          </ul>
        </details>
      ) : null}
    </section>
  );
}

// ---- Views -----------------------------------------------------------------

// The fleet, reduced to one number for the hero: hosted brains and how many
// need attention. The Fleet view has the full table.
function useFleetCount() {
  const [state, setState] = useState(null);
  useEffect(() => {
    let alive = true;
    apiGet('/apps/admin/instances')
      .then((r) => {
        if (!alive) return;
        const list = (r.instances || r || []).filter((i) => i.status !== 'terminated');
        const bad = (i) => ['populated', 'updating', 'current'].some((k) => i.monitor?.brain?.verdicts?.[k] && !i.monitor.brain.verdicts[k].ok);
        setState({ total: list.length, withBrain: list.filter((i) => i.monitor?.brain).length, attention: list.filter(bad).length });
      })
      .catch(() => { if (alive) setState({ total: null, withBrain: null, attention: 0 }); });
    return () => { alive = false; };
  }, []);
  return state;
}

function Overview({ summary, pages, onTag, liveness, graph, ctx }) {
  const navigate = useNavigate();
  const fleet = useFleetCount();
  const issuesByKind = useMemo(() => {
    const m = {};
    for (const i of summary.issues || []) (m[i.kind] = m[i.kind] || []).push(i);
    return Object.entries(m).sort((a, b) => SEV_ORDER[a[1][0].severity] - SEV_ORDER[b[1][0].severity] || b[1].length - a[1].length);
  }, [summary]);
  const tags = useMemo(() => Object.entries(summary.tags || {}).sort((a, b) => b[1] - a[1]), [summary]);
  const maxTag = tags[0]?.[1] || 1;
  const covered = (summary.coverage || []).reduce((a, c) => a + c.covered, 0);
  const total = (summary.coverage || []).reduce((a, c) => a + c.total, 0);
  const recent = useMemo(() => [...(summary.pages || [])].filter((p) => p.updated).sort((a, b) => b.updated.localeCompare(a.updated)).slice(0, 8), [summary]);

  // The hero: the brain's architecture as lobes with one live number each,
  // and the wiki as a hemisphere of types fanning out to its hub pages.
  const l = liveness || {};
  const heat = ctx?.context?.heat || null;
  const errors = summary.issueCounts?.error ?? 0;
  const warns = summary.issueCounts?.warn ?? 0;
  const inboxN = (l.inboxPending || 0) + (l.runtimeInboxPending || 0);
  const indexSections = (summary.index || []).length;
  const indexed = (summary.index || []).reduce((a, sec) => a + (sec.entries || []).length, 0);
  const novaLive = !!(heat?.until && Date.now() - heat.until < 3600000);
  const lobes = [
    { id: 'index', label: 'Index', value: indexed || summary.totals?.pages, sub: `${indexSections} sections · AGENTS.md always on`, onOpen: () => navigate('/admin/brain/index') },
    { id: 'log', label: 'Log', value: summary.totals?.logEntries ?? 0, sub: l.lastLogEntry ? `last entry ${l.lastLogEntry}` : 'no dated entry', onOpen: () => navigate('/admin/brain/log') },
    { id: 'findings', label: 'Findings', value: errors + warns, sub: `${errors} errors · ${warns} warnings`, tone: errors ? 'err' : warns ? 'warn' : 'ok', onOpen: () => document.getElementById('ab-attention')?.scrollIntoView({ behavior: 'smooth', block: 'start' }) },
    { id: 'coverage', label: 'Code catalogued', value: total ? `${Math.round((covered / total) * 100)}%` : '—', sub: `${covered}/${total} source files`, tone: total && covered / total < 0.7 ? 'warn' : undefined, onOpen: () => navigate('/admin/brain/coverage') },
    { id: 'inbox', label: 'Inbox', value: inboxN, sub: `${l.inboxPending ?? 0} files · ${l.runtimeInboxPending ?? 0} runtime drafts`, tone: inboxN >= 5 ? 'warn' : undefined, onOpen: () => navigate('/admin/brain/schema') },
    { id: 'corpus', label: 'Corpus', value: l.mirrorDocs ?? '—', sub: l.mirrorPending ? `${l.mirrorPending} waiting to index` : 'knowledge/brain-* indexed', tone: l.mirrorPending ? 'warn' : 'ok', onOpen: () => navigate('/admin/brain/context') },
    { id: 'nova', label: 'NoVA answers', value: ctx ? (heat ? heat.traces : ctx.error ? '—' : '…') : '…', sub: heat ? `${heat.distinctDocs} pages served · watch it think` : 'last 200 traces · watch it think', live: novaLive, onOpen: () => navigate('/admin/brain/think') },
    { id: 'fleet', label: 'Hosted brains', value: fleet ? (fleet.withBrain ?? '—') : '…', sub: fleet ? `${fleet.total ?? '—'} instances · ${fleet.attention} need attention` : 'phoned home', tone: fleet?.attention ? 'warn' : undefined, onOpen: () => navigate('/admin/brain/fleet') },
  ];
  const types = TYPE_ORDER.map((t) => ({ type: t, count: summary.byType?.[t] || 0, onOpen: () => navigate(`/admin/brain/type/${t}`) }));
  const hubs = useMemo(() => {
    const nodes = [...(graph?.nodes || [])].sort((a, b) => (b.backlinks || 0) - (a.backlinks || 0));
    return nodes.filter((n) => TYPE_ORDER.includes(n.type)).map((n) => ({ key: n.slug, slug: n.slug, docId: `brain-${n.slug}`, title: n.title, type: n.type, backlinks: n.backlinks || 0, stale: n.stale, thin: n.thin, errors: n.errors, onOpen: () => navigate(`/admin/brain/page/${n.slug}`) }));
  }, [graph, navigate]);

  return (
    <>
      <BrainHero
        name="The plane"
        sub={l.syncedAt ? `synced ${fmtAgo(l.syncedAt)}${l.gitSha ? ` · ${String(l.gitSha).slice(0, 7)}` : ''}` : ''}
        pages={summary.totals?.pages ?? 0}
        verdicts={planeVerdicts(summary, liveness)}
        onOpenCenter={() => navigate('/admin/brain/map')}
        lobes={lobes}
        wiki={{ types, hubs }}
        heat={heat?.heat || null}
        note="Click the nucleus for the map; a lobe opens its view; a type opens its list; a satellite opens that page."
      />

      <LivenessCard summary={summary} liveness={liveness} />

      <div className="ab-grid">
        <section className="cs-card ab-card" id="ab-attention">
          <h2>Needs attention <span className="cs-help">{(summary.issues || []).length} findings across {new Set((summary.issues || []).map((i) => i.page)).size} pages</span></h2>
          {issuesByKind.length === 0 ? <p className="cs-help">Clean — nothing to fix.</p> : null}
          {issuesByKind.map(([kind, list]) => (
            <details key={kind} className="ab-issue-group" open={list[0].severity === 'error'}>
              <summary>
                <Sev s={list[0].severity} />
                <span className="ab-issue-kind">{KIND_LABEL[kind] || kind}</span>
                <span className="ab-count">{list.length}</span>
              </summary>
              <ul className="ab-issue-list">
                {list.map((i, idx) => (
                  <li key={`${i.page}-${idx}`}>
                    <PageLink slug={i.page} pages={pages} />
                    <span className="ab-issue-msg">{i.message}</span>
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </section>

        <div className="ab-col">
          <section className="cs-card ab-card">
            <h2>Recently updated</h2>
            <ul className="ab-plain">
              {recent.map((p) => (
                <li key={p.slug}>
                  <span className="ab-date">{p.updated}</span>
                  <PageLink slug={p.slug} pages={pages}>{p.title}</PageLink>
                  <span className={`ab-type is-${p.type}`}>{p.type}</span>
                </li>
              ))}
            </ul>
          </section>
          <section className="cs-card ab-card">
            <h2>Tags <span className="cs-help">click to filter</span></h2>
            <div className="ab-tags">
              {tags.map(([t, n]) => (
                <button key={t} type="button" className="ab-tagbtn" style={{ opacity: 0.55 + 0.45 * (n / maxTag) }} onClick={() => onTag(t)}>
                  {t} <span className="ab-count">{n}</span>
                </button>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

function Coverage({ summary, schema, pages }) {
  const areas = summary.coverage || [];
  const files = schema?.coverageFiles || [];
  const missingSections = (summary.issues || []).filter((i) => i.kind === 'missing-section');
  const staleSources = (summary.issues || []).filter((i) => i.kind === 'source-missing');
  return (
    <>
      <section className="cs-card ab-card">
        <h2>Code the brain never mentions <span className="cs-help">a source file counts as catalogued when any wiki page names it (body or sources)</span></h2>
        {areas.map((a) => {
          const pct = a.total ? Math.round((a.covered / a.total) * 100) : 100;
          const detail = files.find((f) => f.key === a.key);
          return (
            <details key={a.key} className="ab-cov" open={pct < 60}>
              <summary>
                <span className="ab-cov-label">{a.label}</span>
                <span className="ab-bar"><span className={`ab-bar-fill ${pct < 60 ? 'is-warn' : ''}`} style={{ width: `${pct}%` }} /></span>
                <span className="ab-cov-num">{a.covered}/{a.total}</span>
                <span className="ab-count">{a.uncatalogued.length} gaps</span>
              </summary>
              {a.uncatalogued.length ? (
                <ul className="ab-files">
                  {a.uncatalogued.map((f) => <li key={f} className="aa-mono">{f}</li>)}
                </ul>
              ) : <p className="cs-help">Every file here is referenced by at least one page.</p>}
              {detail ? (
                <details className="ab-sub">
                  <summary className="cs-help">covered files → which pages mention them</summary>
                  <ul className="ab-files">
                    {detail.files.filter((f) => f.mentionedBy.length).map((f) => (
                      <li key={f.file}>
                        <span className="aa-mono">{f.file}</span>
                        <span className="ab-mentions">{f.mentionedBy.map((s) => <PageLink key={s} slug={s} pages={pages} />)}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
            </details>
          );
        })}
      </section>

      <div className="ab-grid">
        <section className="cs-card ab-card">
          <h2>Pages missing schema sections <span className="cs-help">{missingSections.length}</span></h2>
          {missingSections.length === 0 ? <p className="cs-help">Every typed page has its required sections.</p> : null}
          <ul className="ab-issue-list">
            {missingSections.map((i, idx) => (
              <li key={idx}><PageLink slug={i.page} pages={pages} /><span className="ab-issue-msg">{i.message}</span></li>
            ))}
          </ul>
        </section>
        <section className="cs-card ab-card">
          <h2>Sources that drifted from the code <span className="cs-help">{staleSources.length}</span></h2>
          {staleSources.length === 0 ? <p className="cs-help">Every cited source path still exists.</p> : null}
          <ul className="ab-issue-list">
            {staleSources.map((i, idx) => (
              <li key={idx}><PageLink slug={i.page} pages={pages} /><span className="ab-issue-msg aa-mono">{i.message.replace('source path no longer exists: ', '')}</span></li>
            ))}
          </ul>
        </section>
      </div>
    </>
  );
}

function LogView({ log, pages }) {
  const [kind, setKind] = useState('');
  const entries = (log?.entries || []).filter((e) => !kind || e.kind === kind);
  const kinds = useMemo(() => [...new Set((log?.entries || []).map((e) => e.kind).filter(Boolean))], [log]);
  return (
    <section className="cs-card ab-card">
      <h2>
        Log <span className="cs-help">{entries.length} entries, newest first</span>
        <span className="ab-filters">
          <button type="button" className={`ab-chip ${!kind ? 'is-active' : ''}`} onClick={() => setKind('')}>all</button>
          {kinds.map((k) => <button key={k} type="button" className={`ab-chip ${kind === k ? 'is-active' : ''}`} onClick={() => setKind(k)}>{k}</button>)}
        </span>
      </h2>
      <ol className="ab-log">
        {entries.map((e, i) => (
          <li key={i} className={e.malformed ? 'is-malformed' : ''}>
            <div className="ab-log-head">
              <span className="ab-date">{e.date || '????-??-??'}</span>
              <span className={`ab-chip is-static is-${e.kind}`}>{e.kind || 'malformed'}</span>
              <strong>{e.title}</strong>
            </div>
            <Markdown text={e.body} pages={pages} />
          </li>
        ))}
      </ol>
    </section>
  );
}

function IndexView({ index, pages }) {
  return (
    <section className="cs-card ab-card">
      <h2>index.md <span className="cs-help">the catalog agents read first — red entries point at pages that don't exist</span></h2>
      {(index?.sections || []).map((s) => (
        <div key={s.title} className="ab-index-sec">
          <h3>{s.title} <span className="ab-count">{s.entries.length}</span></h3>
          <ul className="ab-plain">
            {s.entries.map((e) => (
              <li key={e.slug}>
                <PageLink slug={e.slug} pages={pages} />
                <span className="ab-blurb">{e.blurb}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

// The runtime's side of the inbox (brainInbox/*, shared/brainInbox.js):
// drafts the plane filed itself — closed incidents, self-test flips — plus
// what an operator types here. `node scripts/drain-brain-inbox.mjs` turns
// pending notes into brain/inbox/*.md files for the ingest skill.
const INBOX_KIND_LABEL = { incident: 'incident', console: 'note', selftest: 'self-test', plane: 'from plane' };
function RuntimeInbox() {
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => db.collection('brainInbox').where('status', '==', 'pending').limit(50).onSnapshot(
    (snap) => setNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))),
    (e) => setErr(e.message),
  ), []);
  const file = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setBusy(true); setErr('');
    try {
      const firstLine = body.split('\n')[0].replace(/^#+\s*/, '').slice(0, 120);
      await db.collection('brainInbox').add({
        status: 'pending', kind: 'console', title: firstLine, body, refs: [], slug: null, source: 'console',
        forInstance: false, related: [], createdAt: Date.now(), createdBy: auth.currentUser?.email || auth.currentUser?.uid || null,
      });
      setText('');
    } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  };
  return (
    <section className="cs-card ab-card">
      <h2>Runtime inbox <span className="cs-help">{notes ? `${notes.length} pending` : '…'} · drafts the plane filed itself + notes from this console</span></h2>
      <form onSubmit={file} className="ab-capture">
        <textarea className="cs-input" rows={3} value={text} onChange={(e) => setText(e.target.value)} disabled={busy}
          placeholder="Something you just learned — first line becomes the title. Lands in brain/inbox on the next drain; the ingest skill files it as a lesson." />
        <div className="ab-capture-row">
          <span className="cs-help">node scripts/drain-brain-inbox.mjs</span>
          <button type="submit" className="cs-btn cs-btn-primary" disabled={busy || !text.trim()}>{busy ? 'Filing…' : 'File to inbox'}</button>
        </div>
      </form>
      {err ? <p className="cs-help" role="alert">{err}</p> : null}
      {notes?.length ? (
        <ul className="ab-files ab-notes">
          {notes.map((n) => (
            <li key={n.id}>
              <span className="ab-note-kind">{INBOX_KIND_LABEL[n.kind] || n.kind}</span>
              <span>{n.title}</span>
              {n.slug ? <span className="aa-mono cs-help">{n.slug}</span> : null}
              <span className="cs-help">{fmtAgo(n.createdAt ? new Date(n.createdAt).toISOString() : null)}</span>
              {n.related?.length ? <span className="cs-help">↳ {n.related.map((r) => <Link key={r.slug} to={`${PAGE_PATH}${r.slug}`}>{r.title}</Link>).reduce((acc, el, i) => (i ? [...acc, ', ', el] : [el]), [])}</span> : null}
            </li>
          ))}
        </ul>
      ) : notes ? <p className="cs-help">Nothing pending — the plane has filed no drafts since the last drain.</p> : null}
    </section>
  );
}

function SchemaView({ schema, pages }) {
  return (
    <>
      <section className="cs-card ab-card">
        <h2>Skills <span className="cs-help">reusable workflows agents run by name</span></h2>
        {(schema?.skills || []).map((s) => (
          <details key={s.name} className="ab-skill">
            <summary><span className="aa-mono">brain/skills/{s.name}.md</span></summary>
            <Markdown text={s.body} pages={pages} />
          </details>
        ))}
      </section>
      <RuntimeInbox />
      <section className="cs-card ab-card">
        <h2>Inbox <span className="cs-help">{schema?.inbox?.pending?.length || 0} pending · {schema?.inbox?.processed?.length || 0} processed</span></h2>
        {schema?.inbox?.pending?.length ? <ul className="ab-files">{schema.inbox.pending.map((f) => <li key={f} className="aa-mono">brain/inbox/{f}</li>)}</ul> : <p className="cs-help">Nothing waiting to be ingested.</p>}
        {schema?.inbox?.processed?.length ? (
          <details className="ab-sub"><summary className="cs-help">processed sources</summary>
            <ul className="ab-files">{schema.inbox.processed.map((f) => <li key={f} className="aa-mono">{f}</li>)}</ul>
          </details>
        ) : null}
      </section>
      <section className="cs-card ab-card">
        <h2>SCHEMA.md</h2>
        <Markdown text={schema?.schema} pages={pages} />
      </section>
    </>
  );
}

function PageReader({ slug, pages, summary, graph }) {
  const navigate = useNavigate();
  const page = pages[slug];
  if (!page) {
    return (
      <section className="cs-card ab-card">
        <h2>[[{slug}]]</h2>
        <p className="cs-help">No page with this slug. Referenced by:</p>
        <ul className="ab-plain">
          {Object.values(pages).filter((p) => (p.links || []).includes(slug)).map((p) => <li key={p.slug}><PageLink slug={p.slug} pages={pages}>{p.title}</PageLink></li>)}
        </ul>
      </section>
    );
  }
  const issues = (summary?.issues || []).filter((i) => i.page === slug);
  return (
    <article className="ab-reader">
      <header className="ab-reader-head">
        <div className="ab-reader-meta">
          <span className={`ab-type is-${page.type}`}>{page.type}</span>
          <span className={`ab-chip is-static ${page.status === 'superseded' ? 'is-superseded' : ''}`}>{page.status || 'no status'}</span>
          <span className="ab-chip is-static">confidence: {page.confidence || '—'}</span>
          <span className="ab-chip is-static">updated {page.updated || '—'}</span>
          <span className="ab-chip is-static">{page.wordCount} words</span>
          <span className="aa-mono ab-path">{page.path}</span>
        </div>
        <div className="ab-tags">{(page.tags || []).map((t) => <Link key={t} to={`/admin/brain/tag/${encodeURIComponent(t)}`} className="ab-tagbtn">{t}</Link>)}</div>
        {issues.length ? (
          <ul className="ab-issue-list ab-reader-issues">
            {issues.map((i, idx) => <li key={idx}><Sev s={i.severity} /><span className="ab-issue-msg">{i.message}</span></li>)}
          </ul>
        ) : null}
      </header>
      <Markdown text={page.body} pages={pages} />
      {graph ? (
        <section className="ab-reader-hood">
          <h4>Neighbourhood <span className="cs-help">who links here, what this links to — <Link to="/admin/brain/map">open the map</Link></span></h4>
          <BrainNeighbourhood graph={graph} focus={slug} onOpen={(n) => navigate(`/admin/brain/page/${n.slug}`)} />
        </section>
      ) : null}
      <footer className="ab-reader-foot">
        <div>
          <h4>Links out <span className="ab-count">{(page.links || []).length}</span></h4>
          <div className="ab-linkrow">{(page.links || []).map((l) => <PageLink key={l} slug={l} pages={pages} />)}</div>
        </div>
        <div>
          <h4>Backlinks <span className="ab-count">{(page.backlinks || []).length}</span></h4>
          <div className="ab-linkrow">{(page.backlinks || []).map((l) => <PageLink key={l} slug={l} pages={pages} />)}</div>
        </div>
        <div>
          <h4>Sources</h4>
          <ul className="ab-files">{(page.sources || []).map((s) => <li key={s} className="aa-mono">{s}</li>)}</ul>
        </div>
      </footer>
    </article>
  );
}

// Search runs server-side (GET /admin/brain/search → shared/brainScore.js),
// the same scorer as scripts/brain-search.mjs and NoVA's search_brain tool.
// The context picture + retrieval heat (GET /admin/brain/context).
function useBrainContext(enabled) {
  const [state, setState] = useState({ context: null, error: '' });
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    apiGet('/admin/brain/context').then((c) => { if (live) setState({ context: c, error: '' }); }).catch((e) => { if (live) setState({ context: null, error: e.message }); });
    return () => { live = false; };
  }, [enabled]);
  return state;
}

// ---- Think: watch the brain think ------------------------------------------------
// Ask a question and watch the search animate over the map as it happens
// (/voice/chat with stream: true — the same NoVA text path, so search_brain
// and ask_knowledge both animate), replay any recent answer from
// brainTraces (GET /admin/brain/traces), or tail new answers live for a
// wall display. [[watch-the-brain-think]]
const fmtWhen = (ms) => { const d = new Date(ms); return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`; };
function ThinkView({ graph, traceId, heat = null }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [events, setEvents] = useState([]); // the live stream of the current ask
  const [status, setStatus] = useState('');
  const [answer, setAnswer] = useState(null); // { reply, citations, trace }
  const [error, setError] = useState('');
  const [cmp, setCmp] = useState(null); // { mode: 'test'|'again', busy, a, b, error } — the two tests of a finished search
  const [recent, setRecent] = useState([]);
  const [replay, setReplay] = useState(null); // a brainTraces doc being replayed
  const [live, setLive] = useState(false);
  const [inflight, setInflight] = useState([]); // brainLive mirrors: searches running right now
  const [watch, setWatch] = useState(null); // Live mode's subject: { id, question, surface, at, events, done, final }
  const seenRef = useRef(0);
  const recentRef = useRef([]);
  const watchedRef = useRef(new Set()); // ids seen in flight — their finished trace updates in place, never replays
  const askIdRef = useRef(null); // the trace id of our own last ask

  const loadRecent = useCallback(async () => {
    try { const r = await apiGet('/admin/brain/traces?limit=30'); setRecent(r.traces || []); } catch { /* the list is optional */ }
  }, []);
  useEffect(() => { loadRecent(); }, [loadRecent]);
  useEffect(() => { recentRef.current = recent; }, [recent]);
  // A deep link (/admin/brain/think/<traceId>) selects that trace once the
  // list is in; applied once per id so clearing or asking is not undone.
  const appliedRef = useRef(null);
  useEffect(() => {
    if (!traceId || appliedRef.current === traceId) return;
    const t = recent.find((x) => x.id === traceId);
    if (t) { appliedRef.current = traceId; setReplay(t); setAnswer(null); setEvents([]); }
  }, [traceId, recent]);

  // Live: two polls. Every 2 s, the searches in flight (brainLive mirrors —
  // anyone's: NoVA turns, voice, the search box, GraphQL) — the newest one
  // plays as it grows, concurrent ones are chips to switch between. Every
  // 5 s, finished traces newer than the newest seen: one watched in flight
  // updates in place (the player rests on its final state); one nobody
  // watched replays. A wall display can sit on this all day.
  useEffect(() => {
    if (!live) { setInflight([]); setWatch(null); return undefined; }
    let on = true;
    seenRef.current = Math.max(seenRef.current, ...recentRef.current.map((t) => t.at || 0), 0);
    setReplay(null); setAnswer(null); setEvents([]);
    const pollLive = async () => {
      try {
        const r = await apiGet('/admin/brain/live');
        if (!on) return;
        const list = r.live || [];
        list.forEach((s) => watchedRef.current.add(s.id));
        setInflight(list);
        // The list is newest-activity first. Follow the most active search
        // unless the user pinned one (a chip); a search that went quiet
        // while another runs is not the one to watch.
        setWatch((w) => {
          const same = w && list.find((s) => s.id === w.id);
          if (same && (w.pinned || list[0].id === w.id)) return { ...same, pinned: w.pinned, done: false, final: false };
          if (list.length) return { ...list[0], done: false, final: false };
          if (w && !w.done) return { ...w, done: true }; // it just finished — hold until its record lands
          return w;
        });
      } catch { /* try again next tick */ }
    };
    const pollTraces = async () => {
      try {
        const r = await apiGet(`/admin/brain/traces?limit=5&after=${seenRef.current}`);
        const fresh = (r.traces || []).filter((t) => (t.at || 0) > seenRef.current);
        if (!on || !fresh.length) return;
        seenRef.current = Math.max(...fresh.map((t) => t.at || 0));
        setRecent((prev) => [...fresh.filter((t) => !prev.some((p) => p.id === t.id)), ...prev].slice(0, 30));
        setWatch((w) => {
          const mine = w && fresh.find((t) => t.id === w.id);
          if (mine) return { ...w, events: mine.think?.events || w.events, total: mine.think?.total, ms: mine.ms, hits: mine.hits, done: true, final: true };
          if (w && !w.done) return w; // never hijack a search being watched
          const unseen = fresh.find((t) => !watchedRef.current.has(t.id));
          return unseen ? { id: unseen.id, question: unseen.question, surface: unseen.surface, at: unseen.at, ms: unseen.ms, hits: unseen.hits, events: unseen.think?.events || [], total: unseen.think?.total, done: true, final: true } : w;
        });
      } catch { /* try again next tick */ }
    };
    pollLive();
    const a = setInterval(pollLive, 2000);
    const b = setInterval(pollTraces, 5000);
    return () => { on = false; clearInterval(a); clearInterval(b); };
  }, [live]);

  const ask = async (e) => {
    e?.preventDefault();
    const text = q.trim();
    if (!text || busy) return;
    setBusy(true); setError(''); setAnswer(null); setReplay(null); setEvents([]); setStatus('Thinking'); askIdRef.current = null;
    if (traceId) navigate('/admin/brain/think');
    try {
      const user = auth.currentUser;
      const token = user && (await user.getIdToken());
      const res = await fetch(`${backendBase()}/voice/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ stream: true, messages: [{ role: 'user', content: text }], page: '/admin/brain/think', orgId: '' }),
      });
      if (!res.ok) throw new Error(`chat failed (${res.status})`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      let done = null;
      const onEvent = (ev) => {
        if (ev.type === 'status' && ev.text) setStatus(ev.text);
        else if (ev.type === 'think' && ev.event) setEvents((prev) => [...prev, ev.event]);
        else if (ev.type === 'done') done = ev;
        else if (ev.type === 'error') throw new Error(ev.error || 'chat failed');
      };
      for (;;) {
        const { value, done: end } = await reader.read();
        if (end) break;
        buf += dec.decode(value, { stream: true });
        let i;
        while ((i = buf.indexOf('\n')) >= 0) { const line = buf.slice(0, i).trim(); buf = buf.slice(i + 1); if (line) onEvent(JSON.parse(line)); }
      }
      if (buf.trim()) onEvent(JSON.parse(buf.trim()));
      if (done) { askIdRef.current = done.traceId || null; setAnswer({ reply: done.reply || '', citations: done.citations || [], trace: done.trace || null }); }
      loadRecent();
    } catch (err) {
      setError(err.message || 'chat failed');
    } finally {
      setBusy(false); setStatus('');
    }
  };

  const hrefFor = (docId) => citationHref({ docId });
  // Our own ask streams straight in (NDJSON); in Live mode the same search
  // also comes round through brainLive under its trace id — keep the local
  // stream and the key, so the player neither lags nor restarts.
  const ownAsk = busy || (watch && watch.id && watch.id === askIdRef.current);
  const playing = ownAsk ? events : watch ? watch.events || [] : replay ? replay.think?.events || [] : events;
  const showLive = ownAsk ? busy : watch ? !watch.done : false;
  const playerKey = ownAsk ? 'ask' : watch ? `live-${watch.id}` : replay?.id || 'ask';

  // The two tests of a finished search (POST /admin/brain/think/again):
  // "test it" re-asks without the pages the answer cited — did the
  // citations carry the answer?; "ask again today" re-asks as is — what has
  // the brain learned since? Both compare the recorded stream with the new one.
  const subject = !showLive && playing.length ? { question: ownAsk ? q.trim() || answer?.trace?.variants?.[0] : watch ? watch.question : replay?.question, events: playing, reply: ownAsk ? answer?.reply : null } : null;
  const citedNow = [...(subject?.events || [])].reverse().find((e) => e.type === 'answer.done')?.citations || [];
  useEffect(() => { setCmp(null); }, [playerKey]);
  const runTest = async (mode) => {
    if (!subject?.question) return;
    const withhold = mode === 'test' ? citedNow : [];
    setCmp({ mode, busy: true, a: null, b: null, error: '' });
    try {
      const r = await apiPost('/admin/brain/think/again', { question: subject.question, withhold });
      const label = (d) => new Date(d || Date.now()).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      setCmp({
        mode, busy: false, error: '',
        a: { label: mode === 'test' ? 'as answered' : `recorded ${label(replay?.at || watch?.at)}`, events: subject.events, reply: subject.reply, citations: citedNow },
        b: { label: mode === 'test' ? 'without the cited pages' : 'today', events: r.trace?.think?.events || [], reply: r.reply, citations: r.citations, withheld: r.withheld || [] },
      });
      loadRecent();
    } catch (err) {
      setCmp({ mode, busy: false, a: null, b: null, error: err.message || 'failed' });
    }
  };
  return (
    <>
      <section className="cs-card ab-card ab-mapcard" data-nova-note="Think: ask a question and watch the brain search, or replay a recent answer">
        <h2>Think <span className="cs-help">watch the brain search — pages light up as they are found, kept, read and cited</span></h2>
        <form className="ab-think-ask" onSubmit={ask}>
          <input className="cs-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask the brain something…" aria-label="Question" disabled={busy} />
          <button type="submit" className="cs-btn cs-btn-primary" disabled={busy || !q.trim()}>{busy ? status || 'Thinking…' : 'Ask'}</button>
          <label className="ab-think-live"><input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} /> Live — watch every search of the brain as it runs</label>
        </form>
        {error ? <div className="cs-banner b-danger">{error}</div> : null}
        {live ? (
          <p className="cs-help ab-think-replaying">
            {inflight.length ? (
              <>
                <span className="cs-dot live" /> {inflight.length === 1 ? '1 search' : `${inflight.length} searches`} in flight:
                {inflight.map((s) => (
                  <button key={s.id} type="button" className={`cs-btn cs-btn-sm ${watch?.id === s.id ? 'cs-btn-primary' : 'cs-btn-ghost'}`} onClick={() => setWatch({ ...s, pinned: true, done: false, final: false })} title={s.surface || ''}>
                    {s.question ? `“${s.question.slice(0, 48)}${s.question.length > 48 ? '…' : ''}”` : s.surface || 'a search'} · {s.total || s.events?.length || 0}
                  </button>
                ))}
              </>
            ) : watch ? (
              <>{watch.final ? 'Finished' : 'Finishing'} {watch.question ? <>“{watch.question}”</> : 'a search'}{watch.surface ? ` · ${watch.surface}` : ''}{watch.ms ? ` · ${(watch.ms / 1000).toFixed(1)}s` : ''} · waiting for the next search</>
            ) : 'Watching — nothing is searching the brain right now. Ask NoVA anywhere, search the brain, or ask above.'}
          </p>
        ) : null}
        {replay && !watch ? (
          <p className="cs-help ab-think-replaying">
            Replaying {replay.question ? <>“{replay.question}”</> : 'an answer'} · {fmtWhen(replay.at)}{replay.surface ? ` · ${replay.surface}` : ''}{replay.ms ? ` · ${(replay.ms / 1000).toFixed(1)}s` : ''}
            {' '}<button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => { setReplay(null); navigate('/admin/brain/think'); }}>clear</button>
          </p>
        ) : null}
        {playing.length || showLive ? (
          <BrainThink key={playerKey} graph={graph} events={playing} live={showLive} height={520} hrefFor={hrefFor} LinkComponent={Link} onOpen={(n) => navigate(`/admin/brain/page/${n.slug}`)} heat={heat} trails={recent} />
        ) : (
          <div className="cs-empty">Ask a question above, pick a recent answer below, or turn on Live to watch every search of the brain — NoVA turns, voice, the search box, the API — as they run. The map lights up as a search runs: faint rings are candidates, teal is kept for the answer, white is cited, amber is a page NoVA opened. Switch views (sonar, curve, race, timing, ribbons, tape, score, heat, trails) with the picker or keys 1–9.</div>
        )}
        {subject?.question ? (
          <div className="ab-think-tests" data-nova-note="Test a finished search: re-ask without its cited pages (were they load-bearing?) or re-ask today (what changed?)">
            <button type="button" className="cs-btn cs-btn-sm" disabled={cmp?.busy || !citedNow.length} onClick={() => runTest('test')} title={citedNow.length ? `Ask again with the ${citedNow.length} cited page${citedNow.length === 1 ? '' : 's'} withheld` : 'This search cited nothing to withhold'}>{cmp?.busy && cmp.mode === 'test' ? 'Testing…' : 'Test it — withhold what it cited'}</button>
            <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={cmp?.busy} onClick={() => runTest('again')} title="Ask the same question against today's corpus">{cmp?.busy && cmp.mode === 'again' ? 'Asking…' : 'Ask again today'}</button>
            <span className="cs-help">like a backtest on unseen data: an answer whose citations were load-bearing loses them when those pages are withheld</span>
          </div>
        ) : null}
        {cmp?.error ? <div className="cs-banner b-danger">{cmp.error}</div> : null}
        {cmp?.a && cmp?.b ? <Compare a={cmp.a} b={cmp.b} mode={cmp.mode} hrefFor={hrefFor} LinkComponent={Link} /> : null}
        {answer && (!watch || ownAsk) ? (
          <div className="ab-think-answer">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{answer.reply}</ReactMarkdown>
            {answer.trace ? <HowIKnew trace={answer.trace} LinkComponent={Link} hrefFor={(h) => citationHref(h)} graph={graph} /> : null}
          </div>
        ) : null}
      </section>
      <section className="cs-card ab-card">
        <h2>Recent searches <span className="cs-help">{recent.length ? `${recent.length} with a think stream — NoVA, voice, the search box, the API — click to replay` : 'nothing recorded yet'}</span></h2>
        <ul className="ab-results ab-think-recent">
          {recent.map((t) => (
            <li key={t.id} className={replay?.id === t.id ? 'is-active' : ''}>
              <button type="button" className="ab-think-row" onClick={() => { setReplay(t); setAnswer(null); setEvents([]); navigate(`/admin/brain/think/${t.id}`); }}>
                <span className="ab-think-q">{t.question || <em>question not recorded</em>}</span>
                <span className="ab-count">{fmtWhen(t.at)}{t.surface ? ` · ${t.surface}` : ''} · {t.think?.total || t.think?.events?.length || 0} steps · {(t.hits || []).length} sources{t.ms ? ` · ${(t.ms / 1000).toFixed(1)}s` : ''}</span>
              </button>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}

function useBrainSearch(q) {
  const [state, setState] = useState({ q: '', hits: [], busy: false });
  useEffect(() => {
    const query = q.trim();
    if (!query) { setState({ q: '', hits: [], busy: false }); return undefined; }
    let live = true;
    setState((s) => ({ ...s, busy: true }));
    const t = setTimeout(() => {
      apiGet(`/admin/brain/search?q=${encodeURIComponent(query)}&top=30`)
        .then((r) => { if (live) setState({ q: query, hits: r.hits || [], busy: false }); })
        .catch(() => { if (live) setState({ q: query, hits: [], busy: false }); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q]);
  return state;
}

function PageList({ title, sub, list, pages }) {
  return (
    <section className="cs-card ab-card">
      <h2>{title} <span className="cs-help">{sub}</span></h2>
      <table className="aa-table ab-table">
        <thead><tr><th>Page</th><th>Type</th><th>Updated</th><th>Conf.</th><th>Words</th><th>Links</th><th>Back</th><th>Findings</th></tr></thead>
        <tbody>
          {list.map((p) => (
            <tr key={p.slug}>
              <td><PageLink slug={p.slug} pages={pages}>{p.title}</PageLink><div className="ab-tags ab-tags-sm">{(p.tags || []).map((t) => <span key={t} className="ab-tagbtn is-static">{t}</span>)}</div></td>
              <td><span className={`ab-type is-${p.type}`}>{p.type}</span></td>
              <td className="aa-mono">{p.updated || '—'}</td>
              <td>{p.confidence || '—'}</td>
              <td>{p.wordCount}</td>
              <td>{p.links}</td>
              <td>{p.backlinks}</td>
              <td>{p.errors ? <span className="ab-sev is-error">{p.errors}</span> : null}{p.issues - p.errors > 0 ? <span className="ab-sev is-warn">{p.issues - p.errors}</span> : null}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

// ---- Page ------------------------------------------------------------------

export default function AdminBrain() {
  const { section = 'overview', slug = '' } = useParams();
  const navigate = useNavigate();
  const { loading, error, summary, pages, log, index, schema, liveness, graph, reload } = useBrain();
  const [q, setQ] = useState('');

  const grouped = useMemo(() => {
    const g = {};
    for (const p of summary?.pages || []) (g[p.type] = g[p.type] || []).push(p);
    return g;
  }, [summary]);
  const search = useBrainSearch(q);
  const results = search.hits;
  const ctx = useBrainContext(section === 'context' || section === 'map' || section === 'overview');

  if (error) {
    return (
      <div className="cs-page ab-page">
        <h1>The Brain</h1>
        <div className="cs-card ab-card"><p className="cs-help">{error}</p></div>
      </div>
    );
  }
  if (loading) return <div className="cs-page ab-page"><h1>The Brain</h1><p className="cs-help">Loading…</p></div>;
  if (!summary) {
    return (
      <div className="cs-page ab-page">
        <h1>The Brain</h1>
        <div className="cs-card ab-card">
          <p className="cs-help">Nothing synced yet. Run <code>node scripts/sync-brain.mjs</code> against this project.</p>
        </div>
      </div>
    );
  }

  const navItem = (key, label, count) => (
    <button key={key} type="button" className={`ab-nav-item ${section === key && !q ? 'is-active' : ''}`} onClick={() => { setQ(''); navigate(key === 'overview' ? '/admin/brain' : `/admin/brain/${key}`); }}>
      {label}{count != null ? <span className="ab-count">{count}</span> : null}
    </button>
  );

  let main;
  if (q.trim()) {
    main = (
      <section className="cs-card ab-card">
        <h2>Search <span className="cs-help">{search.busy ? 'searching…' : `${results.length} pages match “${q}”`}</span></h2>
        <ul className="ab-results">
          {results.map(({ slug: hitSlug, title, type, score, evidence }) => (
            <li key={hitSlug}>
              <div className="ab-result-head">
                <PageLink slug={hitSlug} pages={pages}>{title}</PageLink>
                <span className={`ab-type is-${type}`}>{type}</span>
                <span className="ab-count">score {score}</span>
              </div>
              {(evidence || []).map((line) => <div key={line} className="ab-evidence">{line}</div>)}
            </li>
          ))}
        </ul>
      </section>
    );
  } else if (section === 'page') main = <PageReader slug={slug} pages={pages} summary={summary} graph={graph} />;
  else if (section === 'map') {
    main = (
      <section className="cs-card ab-card ab-mapcard">
        <h2>Map <span className="cs-help">{graph ? `${graph.nodes.length} pages · ${graph.edges.length} wikilinks — clusters by type, size by inbound links, faded when stale or thin` : 'no graph synced yet'}</span></h2>
        <BrainMap
          graph={graph}
          heat={ctx.context?.heat?.heat || null}
          onOpen={(n) => navigate(`/admin/brain/page/${n.slug}`)}
          onSearch={(query) => apiGet(`/admin/brain/search?q=${encodeURIComponent(query)}&top=30`).then((r) => (r.hits || []).map((h) => h.slug))}
          initialSelected={slug || null}
        />
      </section>
    );
  }
  else if (section === 'context') {
    main = (
      <section className="cs-card ab-card ab-mapcard">
        <h2>Context <span className="cs-help">how knowledge reaches NoVA and the builders — live numbers, and what was actually served</span></h2>
        {ctx.error ? <p className="cs-help">{ctx.error}</p> : <ContextMap context={ctx.context} LinkComponent={Link} hrefFor={(x) => (x.slug ? `/admin/brain/page/${x.slug}` : x.docId?.startsWith('platform-') ? `/docs#${x.docId.slice(9)}` : null)} />}
      </section>
    );
  } else if (section === 'think') main = <ThinkView graph={graph} traceId={slug || null} heat={ctx.context?.heat?.heat || null} />;
  else if (section === 'coverage') main = <Coverage summary={summary} schema={schema} pages={pages} />;
  else if (section === 'log') main = <LogView log={log} pages={pages} />;
  else if (section === 'index') main = <IndexView index={index} pages={pages} />;
  else if (section === 'schema') main = <SchemaView schema={schema} pages={pages} />;
  else if (section === 'fleet') main = <FleetBrains />;
  else if (section === 'kit') main = <KitShips />;
  else if (section === 'type') main = <PageList title={TYPE_LABEL[slug] || slug} sub={`${(grouped[slug] || []).length} pages`} list={grouped[slug] || []} pages={pages} />;
  else if (section === 'tag') {
    const tag = decodeURIComponent(slug);
    const list = (summary.pages || []).filter((p) => (p.tags || []).includes(tag));
    main = <PageList title={`#${tag}`} sub={`${list.length} pages`} list={list} pages={pages} />;
  } else main = <Overview summary={summary} pages={pages} liveness={liveness} graph={graph} ctx={ctx} onTag={(t) => navigate(`/admin/brain/tag/${encodeURIComponent(t)}`)} />;

  return (
    <div className="cs-page ab-page" data-nova-note={`The Brain: ${summary.totals?.pages ?? Object.keys(pages || {}).length} pages, section ${section}${slug ? ` (${slug})` : ''}. Map at /admin/brain/map; Think at /admin/brain/think watches a search animate; a page is /admin/brain/page/<slug>; search_brain finds pages, open_brain_page reads one.`}>
      <div className="aa-head">
        <div>
          <h1>The Brain</h1>
          <p className="cs-help">
            Compiled project knowledge — synced {fmtAgo(summary.syncedAt)}{summary.gitSha ? <> from <span className="aa-mono">{summary.gitSha}</span></> : null}.
            {' '}Re-sync with <code>node scripts/sync-brain.mjs</code>.
          </p>
        </div>
        <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={reload}>Refresh</button>
      </div>

      <div className="ab-shell">
        <aside className="ab-rail">
          <input className="cs-input ab-search" placeholder="Search the brain…" value={q} onChange={(e) => setQ(e.target.value)} />
          <nav className="ab-nav">
            {navItem('overview', 'Overview', (summary.issues || []).length)}
            {navItem('coverage', 'Coverage gaps', (summary.coverage || []).reduce((a, c) => a + c.uncatalogued.length, 0))}
            {navItem('map', 'Map', graph ? graph.nodes.length : undefined)}
            {navItem('think', 'Think')}
            {navItem('context', 'Context')}
            {navItem('index', 'Index')}
            {navItem('log', 'Log', summary.totals?.logEntries)}
            {navItem('schema', 'Schema & skills')}
            {navItem('fleet', 'Fleet brains')}
            {navItem('kit', 'Kit ships')}
          </nav>
          {TYPE_ORDER.map((t) => (
            <details key={t} className="ab-group" open={(section === 'type' && slug === t) || (section === 'page' && pages[slug]?.type === t)}>
              <summary>
                <span className={`ab-type is-${t}`}>{TYPE_LABEL[t]}</span>
                <span className="ab-count">{(grouped[t] || []).length}</span>
                <Link to={`/admin/brain/type/${t}`} className="ab-group-all" onClick={(e) => e.stopPropagation()}>table</Link>
              </summary>
              <ul className="ab-group-list">
                {(grouped[t] || []).map((p) => (
                  <li key={p.slug} className={section === 'page' && slug === p.slug ? 'is-active' : ''}>
                    <Link to={`/admin/brain/page/${p.slug}`} onClick={() => setQ('')}>{p.title}</Link>
                    {p.errors ? <span className="ab-dot is-error" title={`${p.errors} error(s)`} /> : p.issues ? <span className="ab-dot is-warn" title={`${p.issues} finding(s)`} /> : null}
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </aside>
        <main className="ab-main">{main}</main>
      </div>
    </div>
  );
}
